/**
 * 聊天服务 - 支持多Agent
 * 根据Agent配置使用不同的API端点
 */

import api, { APIError } from './api';
import type { ChatRequest, Message, SSEvent, SSEEventType, Thought } from '@/types';
import { getAgentById } from '@/config/agents';
import { createSSEClient, type RobustSSEClient } from './sseClient';

function defaultModelProvider(): string {
  return (
    localStorage.getItem('modelProvider') ||
    import.meta.env.VITE_LLM_PROVIDER ||
    'openai'
  );
}

function defaultModel(): string {
  return (
    localStorage.getItem('model') ||
    import.meta.env.VITE_OPENAI_MODEL ||
    import.meta.env.VITE_GPT54_MODEL ||
    'gpt-5.4'
  );
}

function normalizeChatRequest(request: ChatRequest): ChatRequest {
  return {
    ...request,
    model_provider: request.model_provider || defaultModelProvider(),
    model: request.model || defaultModel(),
  };
}

function getAuthHeader(): string | null {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.state?.tokens?.accessToken;
    return token ? `Bearer ${token}` : null;
  } catch {
    return null;
  }
}

function parseChatEvent(raw: string): {
  eventType: string;
  eventData: any;
  content: string;
  timestamp: string;
} | null {
  try {
    const data = JSON.parse(raw);
    const eventType = data.event || data.event_type || data.type || 'message';
    const eventData = data.data ?? data.result ?? {};
    const contentValue = eventData?.content ?? data.content ?? data.message ?? '';
    return {
      eventType,
      eventData,
      content: typeof contentValue === 'string' ? contentValue : '',
      timestamp: data.timestamp || eventData?.timestamp || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * 发送聊天消息（非流式）
 */
export async function sendMessage(request: ChatRequest): Promise<Message> {
  try {
    // 获取agent配置来确定端点
    const agent = request.agent_id ? getAgentById(request.agent_id) : null;
    // 统一走 /juben/chat，由后端按 agent_id 分发，避免旧端点404
    const endpoint = '/juben/chat';
    const authHeader = getAuthHeader();

    const response = await fetch(`${api.base}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(normalizeChatRequest(request)),
    });

    if (!response.ok) {
      throw new APIError(response.status, await response.text());
    }

    if (!response.body) {
      throw new Error('聊天响应为空');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let content = '';
    let metadata: Record<string, unknown> = {};

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const eventText of events) {
        const dataLines = eventText
          .split('\n')
          .filter((line) => line.startsWith('data: '))
          .map((line) => line.slice(6));
        for (const dataLine of dataLines) {
          const parsed = parseChatEvent(dataLine);
          if (!parsed || parsed.eventType === 'heartbeat') continue;
          if (parsed.eventType === 'error' || parsed.eventType === 'workflow_error') {
            throw new Error(parsed.content || parsed.eventData?.error || '发送失败');
          }
          if (parsed.content && ['message', 'llm_chunk', 'data', 'content'].includes(parsed.eventType)) {
            content += parsed.content;
          }
          if (parsed.eventData?.metadata) {
            metadata = { ...metadata, ...parsed.eventData.metadata };
          }
        }
      }
    }

    return {
      id: Date.now().toString(),
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      agentName: agent?.displayName || request.agent_id,
      metadata,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw new Error(`API 错误: ${error.message}`);
    }
    throw error;
  }
}

/**
 * 发送聊天消息（流式）- 支持多Agent（旧版，兼容性保留）
 */
export function streamMessage(
  request: ChatRequest,
  onChunk: (chunk: string) => void,
  onEvent: (event: SSEvent) => void,
  onComplete: () => void,
  onError: (error: Error) => void
): () => void {
  const endpoint = '/juben/chat';
  const normalizedRequest = normalizeChatRequest(request);

  let fullContent = '';
  let closeSSE: (() => void) | null = null;
  let closeRequested = false;

  api.connectSSE(
    endpoint,
    normalizedRequest,
    (event) => {
      const parsed = parseChatEvent(event.data);
      if (!parsed || parsed.eventType === 'heartbeat') {
        return;
      }
      const { eventType, eventData, content, timestamp } = parsed;

      // 处理内容类事件
      if (
        eventType === 'llm_chunk' ||
        eventType === 'message' ||
        eventType === 'data' ||
        eventType === 'content'
      ) {
        if (content) {
          fullContent += content;
          onChunk(content);
        }
      }

      // 处理系统事件
      if (eventType === 'system') {
        onEvent({
          event: 'system',
          data: {
            content: eventData?.content || eventData || '',
            type: 'system',
          },
          timestamp,
        });
      }

      // 处理计费事件
      if (eventType === 'billing') {
        onEvent({
          event: 'billing',
          data: {
            content: eventData?.content || eventData || '',
            type: 'billing',
          },
          timestamp,
        });
      }

      // 处理错误事件
      if (eventType === 'error' || eventType === 'workflow_error') {
        const errorMsg = eventData?.error || eventData?.message || eventData?.content || '未知错误';
        onEvent({
          event: 'error',
          data: {
            content: errorMsg,
            error: errorMsg,
          },
          timestamp,
        });
      }

      // 处理工具事件
      if (
        eventType === 'tool_start' ||
        eventType === 'tool_complete' ||
        eventType === 'tool_processing' ||
        (eventType && eventType.startsWith('tool_'))
      ) {
        onEvent({
          event: eventType as SSEEventType,
          data: eventData,
          timestamp,
        });
      }

      // 检查是否完成
      if (
        eventType === 'done' ||
        eventType === 'complete' ||
        eventType === 'finished' ||
        eventType === 'end'
      ) {
        onComplete();
      }
    },
    onError,
    () => {
      onComplete();
    }
  ).then((close) => {
    closeSSE = close;
    if (closeRequested) {
      closeSSE();
    }
  }).catch(onError);

  return () => {
    closeRequested = true;
    closeSSE?.();
  };
}

/**
 * 发送聊天消息（流式）- 简化版供 chatStore 使用
 */
export async function sendMessageToAgent(
  content: string,
  agentId: string,
  onChunk: (chunk: string, isThought: boolean) => void,
  onThought?: (thought: Thought) => void
): Promise<void> {
  const endpoint = '/juben/chat';

  const request: ChatRequest = {
    input: content,
    agent_id: agentId,
    project_id: localStorage.getItem('projectId') || undefined,
    model_provider: defaultModelProvider(),
    model: defaultModel(),
  };

  let thoughtStep = 0;
  const closeConnection: { current?: () => void } = {};

  try {
    await new Promise<void>((resolve, reject) => {
      api.connectSSE(
        endpoint,
        request,
        (event) => {
          try {
            const parsed = parseChatEvent(event.data);
            if (!parsed || parsed.eventType === 'heartbeat') {
              return;
            }
            const { eventType, eventData, content: parsedContent, timestamp } = parsed;

            const contentValue = parsedContent || eventData || '';
            const contentType = eventData?.content_type || eventData?.type;

            const isThought =
              eventType === 'thinking' ||
              eventType === 'thought' ||
              contentType === 'thought' ||
              contentType === 'plan_step';

            if (typeof contentValue === 'string' && contentValue) {
              onChunk(contentValue, isThought);
              if (isThought && onThought) {
                thoughtStep += 1;
                onThought({
                  step: thoughtStep,
                  content: contentValue,
                  timestamp,
                });
              }
            }
          } catch (error) {
            reject(error instanceof Error ? error : new Error('聊天流事件处理失败'));
          }
        },
        (error) => {
          reject(error);
        },
        () => {
          resolve();
        }
      ).then((close) => {
        closeConnection.current = close;
      }).catch(reject);
    });
  } finally {
    closeConnection.current?.();
  }
}

/**
 * 聊天会话管理器
 * 使用 RobustSSEClient 提供健壮的 SSE 连接、断点续传和自动重连
 */
export class ChatSessionManager {
  private sseClient: RobustSSEClient | null = null;
  private currentRequest: ChatRequest | null = null;
  private eventHandlers: Map<string, Set<Function>> = new Map();

  constructor(config?: {
    maxReconnectAttempts?: number;
    reconnectDelay?: number;
    heartbeatInterval?: number;
    enableCache?: boolean;
  }) {
    this.sseClient = createSSEClient({
      endpoint: '/juben/chat',
      maxReconnectAttempts: config?.maxReconnectAttempts || 3,
      reconnectDelay: config?.reconnectDelay || 2000,
      heartbeatInterval: config?.heartbeatInterval || 30000,
      enableCache: config?.enableCache !== false,
    });

    // 注册事件处理器
    this.sseClient.onEvent((event) => this._handleSSEEvent(event));
    this.sseClient.onError((error) => this._handleError(error));
    this.sseClient.onStateChange((state) => this._handleStateChange(state));
  }

  /**
   * 发送消息
   */
  async sendMessage(
    request: ChatRequest,
    options?: {
      messageId?: string;
      fromSequence?: number;
    }
  ): Promise<void> {
    this.currentRequest = request;

    try {
      await this.sseClient!.connect(request, options);
    } catch (error) {
      this._handleError(error as Error);
      throw error;
    }
  }

  /**
   * 断点续传
   */
  async resume(fromSequence?: number): Promise<boolean> {
    if (!this.sseClient!.getState().currentMessageId) {
      console.warn('[ChatSessionManager] No message ID available for resume');
      return false;
    }

    if (!this.currentRequest) {
      console.warn('[ChatSessionManager] No request available for resume');
      return false;
    }

    try {
      await this.sseClient!.connect(this.currentRequest, {
        messageId: this.sseClient!.getState().currentMessageId,
        fromSequence: fromSequence || this.sseClient!.getState().lastSequence,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    this.sseClient?.disconnect();
  }

  /**
   * 获取当前状态
   */
  getState() {
    return this.sseClient?.getState();
  }

  /**
   * 订阅事件
   */
  on(event: string, handler: Function): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);

    // 返回取消订阅函数
    return () => {
      this.eventHandlers.get(event)?.delete(handler);
    };
  }

  /**
   * 处理 SSE 事件
   */
  private _handleSSEEvent(event: SSEvent): void {
    // 触发 'event' 通用事件
    this._emit('event', event);

    // 根据事件类型触发特定事件
    switch (event.event) {
      case 'message':
        this._emit('message', event.data?.content || '');
        break;

      case 'thinking':
        this._emit('thinking', event.data?.content || '');
        break;

      case 'progress':
        this._emit('progress', event.data);
        break;

      case 'error':
        this._emit('error', new Error(event.data?.content || '未知错误'));
        break;

      case 'complete':
        this._emit('complete', event.data);
        break;

      case 'heartbeat':
        // 心跳事件不触发
        break;

      default:
        this._emit(event.event, event.data);
    }
  }

  /**
   * 处理错误
   */
  private _handleError(error: Error): void {
    this._emit('error', error);

    // 如果是网络错误，尝试重连
    if (this._isNetworkError(error)) {
      const state = this.sseClient!.getState();
      if (state.reconnectAttempts < 3) {
        this._emit('reconnecting', state.reconnectAttempts + 1);
      }
    }
  }

  /**
   * 处理状态变化
   */
  private _handleStateChange(state: any): void {
    this._emit('stateChange', state);

    if (state.isConnected) {
      this._emit('connected');
    } else {
      this._emit('disconnected');
    }
  }

  /**
   * 判断是否是网络错误
   */
  private _isNetworkError(error: any): boolean {
    return (
      error?.name === 'TypeError' ||
      error?.message?.includes('fetch') ||
      error?.message?.includes('network')
    );
  }

  /**
   * 触发事件
   */
  private _emit(event: string, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch {
          // Event handlers are user-land callbacks; keep chat streaming alive if one fails.
        }
      });
    }
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.disconnect();
    this.eventHandlers.clear();
    this.sseClient?.dispose();
  }
}

/**
 * 创建聊天会话（便捷函数）
 */
export function createChatSession(config?: {
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
  enableCache?: boolean;
}): ChatSessionManager {
  return new ChatSessionManager(config);
}

/**
 * 获取聊天历史
 */
export async function getChatHistory(sessionId: string): Promise<Message[]> {
  try {
    const response = await api.get<{ success: boolean; messages?: Message[] }>(
      `/juben/history/${sessionId}`
    );

    return response.messages || [];
  } catch {
    return [];
  }
}

/**
 * 清除聊天历史
 */
export async function clearChatHistory(sessionId: string): Promise<boolean> {
  try {
    const response = await api.delete<{ success: boolean }>(`/juben/history/${sessionId}`);
    return response.success;
  } catch {
    return false;
  }
}

/**
 * 分析意图
 */
export async function analyzeIntent(input: string): Promise<{
  intent: string;
  confidence: number;
  suggestedAgent?: string;
}> {
  try {
    const response = await api.post<{ success: boolean; intent_result?: any }>(
      '/juben/intent/analyze',
      { input }
    );
    const intentResult = response.intent_result || {};

    return {
      intent: intentResult.intent || 'general',
      confidence: intentResult.confidence || 0,
      suggestedAgent: intentResult.suggested_agent,
    };
  } catch {
    return { intent: 'general', confidence: 0 };
  }
}
